import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { inspectEdgeTagCatalog, previewEdgeConnector } from "../../../api";
import ComboInput from "../../../components/ComboInput";
import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import { connectorValidationMessage } from "./EdgeConnectorReview";
import {
  edgeModbusSpecKind,
  edgeModbusSpecToDraft,
  formatEdgeModbusSpec,
  parseEdgeModbusSpecCode,
} from "./edge-connector-code";
import {
  MODBUS_DEVICE_TEMPLATES,
  buildEdgeModbusConnectorSpec,
  edgeConnectorTagEligibility,
  modbusUnitIDBounds,
} from "./edge-connector-spec";

export const SERIAL_BAUD_RATES = [
  "1200", "2400", "4800", "9600", "19200", "38400", "57600", "115200",
].map((rate) => ({ value: rate, label: rate }));

const TAG_TYPE_WIRE = { boolean: "bool", integer: "int64", number: "float64", string: "string" };
const TAG_TYPE_LABELS = { boolean: "Boolean", integer: "Integer", number: "Number", string: "String" };
const MAX_POINTS = 128;
const MODBUS_INTEGER_FORMATS = [
  { value: "int16", label: "Signed 16-bit integer" },
  { value: "uint16", label: "Unsigned 16-bit integer" },
  { value: "int32", label: "Signed 32-bit integer (2 registers)" },
  { value: "uint32", label: "Unsigned 32-bit integer (2 registers)" },
  { value: "int64", label: "Signed 64-bit integer (4 registers)" },
  { value: "uint64", label: "Unsigned 64-bit integer (4 registers)" },
  { value: "bcd16", label: "4-digit BCD (1 register)" },
  { value: "bcd32", label: "8-digit BCD (2 registers)" },
];
const MODBUS_NUMBER_FORMATS = [
  { value: "float32", label: "32-bit floating point (2 registers)" },
  { value: "float64", label: "64-bit floating point (4 registers)" },
  ...MODBUS_INTEGER_FORMATS,
];

// Every format is offered regardless of how the point will be used. Holding
// register commands use FC6 or FC16 according to the selected width.
export const modbusValueTypeOptions = (tagType) =>
  tagType === "number" ? MODBUS_NUMBER_FORMATS : tagType === "integer" ? MODBUS_INTEGER_FORMATS : [];

export const serialPortLabel = (path, hardwareProfile = "") => {
  const value = String(path || "");
  const leaf = value.split("/").filter(Boolean).pop() || value;
  if (leaf.includes("OnLogic_Inc_FR200_Serial_Bridge")) return "OnLogic FR201/FR202 COM port";
  if (hardwareProfile === "compulab-iot-link-v1") {
    if (value === "/dev/ttyLP6") return "IOT-LINK RS-485 port A";
    if (value === "/dev/ttyLP4") return "IOT-LINK RS-485 port B";
  }
  if (value.includes("/dev/serial/by-id/")) return leaf.replace(/^usb-/, "").replace(/_/g, " ");
  return value;
};

export const SERIAL_HARDWARE_GUIDANCE = Object.freeze({
  "onlogic-fr201-v1": Object.freeze({
    missing: "No OnLogic COM port detected",
    portHelp: "Detected on this device. Use the RS-232, RS-422, or RS-485 mode required by the device manual.",
  }),
  "onlogic-fr202-v1": Object.freeze({
    missing: "No OnLogic COM port detected",
    portHelp: "Detected on this device. Use the RS-232, RS-422, or RS-485 mode required by the device manual.",
  }),
  "compulab-iot-link-v1": Object.freeze({
    missing: "No IOT-LINK RS-485 port detected",
    portHelp: "Detected half-duplex RS-485 port. Follow the device manual for A/B wiring and termination.",
  }),
});

const tagSlug = (value) => String(value || "").trim()
  .replace(/[^A-Za-z0-9_]+/g, "_")
  .replace(/^_+|_+$/g, "")
  .slice(0, 64);

const defaultPointSettings = (draft = {}) => {
  const flow = String(draft.flow || "");
  return {
    channel: String(draft.channel ?? "0"),
    // A Turck DXP terminal can be either an input or an output even though
    // both directions use holding-register addressing. Preserve its saved
    // direction on edit and default new channels to the non-actuating mode.
    channelMode: String(draft.channelMode || (["command", "bidirectional"].includes(flow) ? "output" : "input")),
    function: draft.function == null ? "" : String(draft.function),
    address: String(draft.address ?? "0"),
    addressBase: String(draft.addressBase || "zero_based"),
    valueType: String(draft.valueType || ""),
    bit: String(draft.bit ?? "0"),
    byteOrder: String(draft.byteOrder || "big"),
    wordOrder: String(draft.wordOrder || "big"),
    scale: String(draft.scale ?? "1"),
    offset: String(draft.offset ?? "0"),
    pollIntervalMs: String(draft.pollIntervalMs ?? "100"),
    maxReadGap: String(draft.maxReadGap ?? "0"),
    maxReadCount: String(draft.maxReadCount ?? ""),
    failurePolicy: String(draft.failurePolicy || "last_good"),
    writeConfirmation: String(draft.writeConfirmation || "readback"),
    readWindowMode: String(draft.readWindowMode || (draft.readWindowStart != null || draft.readWindowCount != null ? "fixed" : "automatic")),
    readWindowStart: String(draft.readWindowStart ?? draft.address ?? "0"),
    readWindowCount: String(draft.readWindowCount ?? "1"),
    stringLength: String(draft.stringLength ?? "32"),
    stringRaw: Boolean(draft.stringRaw),
    stringRightJustify: Boolean(draft.stringRightJustify),
    alias: String(draft.alias || ""),
    flow,
  };
};

const defaultDeviceSettings = (kind, draft, serialPorts) => {
  const proposed = draft?.settings && typeof draft.settings === "object" ? draft.settings : {};
  return {
    deviceTemplate: String(proposed.deviceTemplate || "generic"),
    host: String(proposed.host || ""),
    port: String(proposed.port ?? "502"),
    serialPort: String(proposed.serialPort || serialPorts.find((path) => String(path).includes("/dev/serial/by-path/")) || serialPorts[0] || ""),
    baudRate: String(proposed.baudRate ?? "19200"),
    dataBits: String(proposed.dataBits ?? "8"),
    stopBits: String(proposed.stopBits ?? "1"),
    parity: String(proposed.parity || "even"),
    unitId: String(proposed.unitId ?? "1"),
    timeoutMs: String(proposed.timeoutMs ?? "2000"),
    // Hidden in the concise form but carried through edits exactly.
    reconnectMinMs: String(proposed.reconnectMinMs ?? "250"),
    reconnectMaxMs: String(proposed.reconnectMaxMs ?? "30000"),
    kind,
  };
};

const initialPoint = (id, draft = null) => {
  const settings = draft?.settings && typeof draft.settings === "object" ? draft.settings : {};
  return {
    id,
    tagMode: draft?.tag_mode === "new" ? "new" : "existing",
    tagId: "",
    tagPath: String(draft?.tag_path || ""),
    newTagName: String(draft?.new_tag_name || ""),
    newTagType: String(draft?.new_tag_type || "boolean"),
    // A device with fifty points wants them under one folder. The form's own
    // "create a tag" path has always rooted a new tag at default/<name>; a
    // draft that came from a device template or from pasted code carries the
    // folder its mappings already name, and dropping it would scatter the
    // device's tags across the project root.
    newTagFolder: String(draft?.new_tag_folder || ""),
    settings: defaultPointSettings(settings),
  };
};

// A tag folder is a path of ordinary segments; each is slugged the same way a
// tag name is, so a folder typed with a space cannot create a path the runtime
// would reject.
const tagFolderSlug = (value) => String(value || "")
  .split("/")
  .map(tagSlug)
  .filter(Boolean)
  .join("/");

const newTagFullPath = (folder, name) => {
  const slug = tagSlug(name);
  if (!slug) return "";
  const prefix = tagFolderSlug(folder);
  return `default/${prefix ? `${prefix}/` : ""}${slug}`;
};

// There is no "read or write?" question here, because Modbus already answers
// it. The address type decides: coils and holding registers are read-write,
// discrete inputs and input registers are read-only by protocol. Asking the
// user to also declare a direction made a second axis that could contradict
// the first, and duplicated a rule the runtime already owns — see
// SourceConfig.Writable in core/drivers/modbus/config.go,
// which modbusPointWritesDevice mirrors. Derive it, then say what was derived.
export const modbusFunctionOptions = (tagType) => [
  { value: "", label: "Automatic — match the tag" },
  { value: "1", label: "Coil (01) — read and write" },
  { value: "2", label: "Discrete input (02) — read only" },
  { value: "3", label: "Holding register (03) — read and write" },
  { value: "4", label: "Input register (04) — read only" },
].filter((option) => tagType === "boolean" || !["1", "2"].includes(option.value));

// Automatic resolves to the natural read-write type for the tag: a coil for
// booleans, a holding register for numbers.
export const modbusEffectiveFunction = (tagType, settings) =>
  Number(settings?.function || (tagType === "boolean" ? 1 : 3));

// Mirrors SourceConfig.Writable: coils carry booleans; holding registers use
// FC6/FC16/FC22 according to the mapped value.
export const modbusPointWritesDevice = (tagType, settings) => {
  const fn = modbusEffectiveFunction(tagType, settings);
  if (fn === 1) return tagType === "boolean";
  if (fn !== 3) return false;
  if (tagType === "boolean") return true;
  if (tagType === "string") return Number(settings?.stringLength || 32) <= 246;
  return MODBUS_NUMBER_FORMATS.some((option) => option.value === settings?.valueType);
};

// The point commands the device only when the address type supports writes AND
// the bound tag is writable. A read-only tag on a coil is not an error — it
// just observes, which is how you hold a writable address read-only.
export const modbusPointDirection = (tagType, settings, tagWritable) => {
  if (settings?.flow === "observe") return "input";
  if (settings?.flow === "command" || settings?.flow === "bidirectional") return "output";
  return modbusPointWritesDevice(tagType, settings) && tagWritable !== false ? "output" : "input";
};

const modbusFlowSummary = (tagType, settings, tagWritable) => {
  if (!tagType) return "";
  if (settings?.flow === "observe" && modbusPointWritesDevice(tagType, settings)) {
    return "Read only — this saved connection remains monitoring-only.";
  }
  if (modbusPointDirection(tagType, settings, tagWritable) === "output") {
    if (settings?.writeConfirmation === "echo") {
      return "Read and write — the device acknowledges writes, but the value is not independently read back.";
    }
    return "Read and write — the tag controls the device, and every write is read back so the tag reflects the device.";
  }
  if (tagWritable === false) return "Read only — this tag is read only on the runtime, so nothing is written to the device.";
  const fn = modbusEffectiveFunction(tagType, settings);
  if (fn === 2 || fn === 4) return "Read only — Modbus has no write function for this address type.";
  return "Read only — this point configuration does not support Modbus writes.";
};

function Field({ label, help, warning, children }) {
  return (
    <div className="gwField">
      <label className="gwLabel">{label}</label>
      {children}
      {help ? <small className="gwHint">{help}</small> : null}
      {warning ? <small className="gwHint isWarning" role="status">{warning}</small> : null}
    </div>
  );
}

const publicConnectionError = (error, gatewayName) => {
  const validation = connectorValidationMessage(error);
  if (validation) return validation;
  const lower = String(error?.message || "").toLowerCase();
  if (/changed since|out of date|revision conflict|refresh .*try again/.test(lower)) {
    return "This connection changed after you opened it. Refresh the page and review your changes again.";
  }
  if (/already (used|claimed|reserved)|resource conflict|in use by/.test(lower)) {
    return "A device point is already used by another connection. Remove the old connection or choose another point.";
  }
  if (/offline|not online|unreachable|not connected/.test(lower)) {
    return `${gatewayName} is offline or unreachable. Reconnect it and try again.`;
  }
  if (/timed out|timeout/.test(lower)) return `${gatewayName} did not respond in time. Check the connection and try again.`;
  return "Control Seat could not prepare this Modbus device. Check its settings and try again.";
};

function ModbusTagField({ point, gateway, locked, eligibility, checking, evidenceError, updatePoint }) {
  const tagType = point.tagMode === "new" ? point.newTagType : eligibility?.tagType || "";
  return (
    <div className="gwConnectionTagPanel">
      <Field label="Tag for this point">
        {point.tagMode === "existing" ? (
          <>
            <div className="gwConnectionTagPickerRow">
              <TagPicker
                value={point.tagId}
                displayValue={point.tagPath}
                placeholder="Search or choose a tag…"
                disabled={locked}
                onChange={(tagId, tagPath) => updatePoint({ tagId, tagPath: tagPath || "" })}
              />
              <button type="button" className="gwSecondaryBtn" onClick={() => updatePoint({ tagMode: "new" })}>
                Create new tag
              </button>
            </div>
            {checking ? <small className="gwHint" role="status">Checking this tag on {gateway.name}…</small> : null}
            {!checking && point.tagPath && eligibility ? (
              <small className={eligibility.eligible ? "gwHint isPositive" : "gwHint isWarning"} role="status">
                {eligibility.eligible ? `Ready on ${gateway.name} · ${TAG_TYPE_LABELS[tagType]}` : eligibility.message}
              </small>
            ) : null}
            {!checking && evidenceError ? <small className="gwHint isWarning" role="status">{evidenceError}</small> : null}
          </>
        ) : (
          <>
            <div className="gwConnectionTagCreateHeader">
              <span><strong>Create a new tag</strong><small>Added to the Tags panel when you connect this device.</small></span>
              <button type="button" className="gwGhostBtn" onClick={() => updatePoint({ tagMode: "existing" })}>Choose existing</button>
            </div>
            <div className="gwConnectionNewTagFields">
              <input
                className="gwInput"
                value={point.newTagName}
                placeholder="New tag name"
                onChange={(event) => updatePoint({ newTagName: event.target.value })}
              />
              <Select
                value={point.newTagType}
                options={Object.entries(TAG_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                onChange={(newTagType) => updatePoint({ newTagType })}
              />
            </div>
            <small className="gwHint">Creates {newTagFullPath(point.newTagFolder, point.newTagName) || "default/…"}.</small>
          </>
        )}
      </Field>
    </div>
  );
}

function ModbusPointFields({ point, turck, tagType, tagWritable, updateSettings }) {
  const fn = modbusEffectiveFunction(tagType, point.settings);
  const registerBit = tagType === "boolean" && (fn === 3 || fn === 4);
  const numeric = tagType === "integer" || tagType === "number";
  const stringPoint = tagType === "string";
  const formats = modbusValueTypeOptions(tagType);
  const fallback = tagType === "number" ? "float32" : "int16";
  const valueType = formats.some((item) => item.value === point.settings.valueType) ? point.settings.valueType : fallback;
  const canEnableSavedControl = !turck && point.settings.flow === "observe" &&
    modbusPointWritesDevice(tagType, { ...point.settings, valueType }) && tagWritable !== false;
  const writesDevice = modbusPointDirection(tagType, { ...point.settings, valueType }, tagWritable) === "output";
  const flowSummary = turck
    ? point.settings.channelMode === "output"
      ? "Output — the tag controls this channel, and every write is read back."
      : "Input — this channel updates the tag and is never written."
    : modbusFlowSummary(tagType, { ...point.settings, valueType }, tagWritable);
  return (
    <>
      <div className="gwConnectionFields">
        {turck ? (
          <>
            <Field label="Channel mode">
              <Select
                value={point.settings.channelMode}
                options={[{ value: "input", label: "Input" }, { value: "output", label: "Output" }]}
                onChange={(channelMode) => updateSettings({ channelMode, flow: "", writeConfirmation: "readback" })}
              />
            </Field>
            <Field label="Digital channel" help="DX0 through DX7; addressing is automatic.">
              <NudgeNumber value={point.settings.channel} onChange={(channel) => updateSettings({ channel })} min={0} max={7} aria-label="Digital channel" />
            </Field>
          </>
        ) : (
          <>
            <Field label="Point type">
              <Select value={point.settings.function} options={modbusFunctionOptions(tagType)} onChange={(value) => updateSettings({ function: value, flow: "", writeConfirmation: "readback" })} />
            </Field>
            <Field label="Point address" help={`Enter the ${point.settings.addressBase === "one_based" ? "one-based" : "zero-based"} address from the device manual.`}>
              <NudgeNumber value={point.settings.address} onChange={(address) => updateSettings({ address })} min={point.settings.addressBase === "one_based" ? 1 : 0} max={point.settings.addressBase === "one_based" ? 65536 : 65535} aria-label="Point address" />
            </Field>
            <Field label="Address numbering" help="Choose the numbering used by the device manual. Control Seat stores the exact protocol address.">
              <Select value={point.settings.addressBase} options={[{ value: "zero_based", label: "Zero-based (first address is 0)" }, { value: "one_based", label: "One-based (first address is 1)" }]} onChange={(addressBase) => updateSettings({ addressBase })} />
            </Field>
            {registerBit ? (
              <Field label="Register bit"><NudgeNumber value={point.settings.bit} onChange={(bit) => updateSettings({ bit })} min={0} max={15} aria-label="Register bit" /></Field>
            ) : null}
            {numeric ? (
              <Field label="Value format"><Select value={valueType} options={formats} onChange={(next) => updateSettings({ valueType: next, flow: "", writeConfirmation: "readback" })} /></Field>
            ) : null}
            {stringPoint ? (
              <Field label="String length (bytes)" help="Use the fixed even byte length documented by the device.">
                <NudgeNumber value={point.settings.stringLength} onChange={(stringLength) => updateSettings({ stringLength })} min={2} max={250} step={2} aria-label="String length (bytes)" />
              </Field>
            ) : null}
          </>
        )}
        <Field label="Poll interval (ms)">
          <NudgeNumber value={point.settings.pollIntervalMs} onChange={(pollIntervalMs) => updateSettings({ pollIntervalMs })} min={50} max={3600000} aria-label="Poll interval (ms)" />
        </Field>
      </div>
      {!turck ? (
        <div className="gwConnectionFields gwModbusPointAdvanced">
          <Field
            label="Read request"
            help={point.settings.readWindowMode === "fixed"
              ? "Use the exact block required by the device manual for polling and write verification."
              : "Automatic reads only the addresses needed by this point."}
          >
            <Select
              value={point.settings.readWindowMode}
              options={[
                { value: "automatic", label: "Automatic range (recommended)" },
                { value: "fixed", label: "Fixed block required by device" },
              ]}
              onChange={(readWindowMode) => updateSettings({ readWindowMode })}
            />
          </Field>
          {point.settings.readWindowMode === "fixed" ? (
            <>
              <Field label="Read block start" help={`${point.settings.addressBase === "one_based" ? "One-based" : "Zero-based"} starting address from the device manual.`}>
                <NudgeNumber value={point.settings.readWindowStart} onChange={(readWindowStart) => updateSettings({ readWindowStart })} min={point.settings.addressBase === "one_based" ? 1 : 0} max={point.settings.addressBase === "one_based" ? 65536 : 65535} aria-label="Read block start" />
              </Field>
              <Field label="Read block size" help="Exact number of coils or registers the device expects.">
                <NudgeNumber value={point.settings.readWindowCount} onChange={(readWindowCount) => updateSettings({ readWindowCount })} min={1} max={fn <= 2 ? 2000 : 125} aria-label="Read block size" />
              </Field>
            </>
          ) : null}
          {point.settings.readWindowMode === "automatic" ? (
            <>
              <Field label="Maximum address gap" help="Merge nearby points into one request across this many unused addresses. Use 0 for contiguous points only.">
                <NudgeNumber value={point.settings.maxReadGap} onChange={(maxReadGap) => updateSettings({ maxReadGap })} min={0} max={fn <= 2 ? 1999 : 124} aria-label="Maximum address gap" />
              </Field>
              <Field label="Maximum values per read" help="Leave blank for the Modbus protocol maximum, or enter a lower device limit.">
                <NudgeNumber value={point.settings.maxReadCount} onChange={(maxReadCount) => updateSettings({ maxReadCount })} min={1} max={fn <= 2 ? 2000 : 125} allowEmpty aria-label="Maximum values per read" />
              </Field>
            </>
          ) : null}
        </div>
      ) : null}
      {numeric || stringPoint ? (
        <div className="gwConnectionFields gwModbusPointAdvanced">
          <Field label="Byte order"><Select value={point.settings.byteOrder} options={[{ value: "big", label: "Big-endian" }, { value: "little", label: "Little-endian" }]} onChange={(byteOrder) => updateSettings({ byteOrder })} /></Field>
          {numeric ? <Field label="Word order"><Select value={point.settings.wordOrder} options={[{ value: "big", label: "High word first" }, { value: "little", label: "Low word first" }]} onChange={(wordOrder) => updateSettings({ wordOrder })} /></Field> : null}
          {tagType === "number" ? (
            <>
              <Field label="Scale" help="Engineering value = raw × scale + offset."><NudgeNumber value={point.settings.scale} onChange={(scale) => updateSettings({ scale })} step={0.1} allowDecimal aria-label="Scale" /></Field>
              <Field label="Offset"><NudgeNumber value={point.settings.offset} onChange={(offset) => updateSettings({ offset })} step={0.1} allowDecimal aria-label="Offset" /></Field>
            </>
          ) : null}
          {stringPoint ? (
            <>
              <Field label="Read padding"><Select value={point.settings.stringRaw ? "raw" : "trim"} options={[{ value: "trim", label: "Trim zero and space padding" }, { value: "raw", label: "Preserve fixed-length padding" }]} onChange={(mode) => updateSettings({ stringRaw: mode === "raw" })} /></Field>
              <Field label="Write alignment"><Select value={point.settings.stringRightJustify ? "right" : "left"} options={[{ value: "left", label: "Left aligned" }, { value: "right", label: "Right aligned" }]} onChange={(alignment) => updateSettings({ stringRightJustify: alignment === "right" })} /></Field>
            </>
          ) : null}
        </div>
      ) : null}
      {!turck && writesDevice ? (
        <div className="gwConnectionFields gwModbusPointAdvanced">
          <Field
            label="Write verification"
            help={point.settings.writeConfirmation === "echo"
              ? "Use only when this device accepts writes but does not support reading the value back. Physical state is not verified."
              : "Recommended. Control Seat reads the value back after each write."}
          >
            <Select
              value={point.settings.writeConfirmation}
              options={[
                { value: "readback", label: "Read the value back (recommended)" },
                { value: "echo", label: "Use the device response only" },
              ]}
              onChange={(writeConfirmation) => updateSettings({ writeConfirmation, flow: "" })}
            />
          </Field>
        </div>
      ) : null}
      {flowSummary ? (
        <div className="gwModbusPointFlow">
          <span>{flowSummary}</span>
          {canEnableSavedControl ? (
            <button type="button" className="gwGhostBtn" onClick={() => updateSettings({ flow: "", writeConfirmation: "readback" })}>
              Enable read and write
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default function EdgeModbusDeviceForm({
  gateway,
  kind,
  serialPorts = [],
  initialDraft = null,
  busy = false,
  notify,
  onCancel,
  onPreview,
}) {
  const nextID = useRef((Array.isArray(initialDraft?.points) && initialDraft.points.length > 0
    ? initialDraft.points.length
    : 1) + 1);
  const [deviceName, setDeviceName] = useState(String(initialDraft?.name || ""));
  const [settings, setSettings] = useState(() => defaultDeviceSettings(kind, initialDraft, serialPorts));
  // A saved device reopens with every point it had. The legacy single-point
  // draft (a GPIO migration, an AI proposal) has no points array, so it still
  // seeds one point from the draft itself.
  const [points, setPoints] = useState(() => (
    Array.isArray(initialDraft?.points) && initialDraft.points.length > 0
      ? initialDraft.points.map((point, index) => initialPoint(`point-${index + 1}`, point))
      : [initialPoint("point-1", initialDraft)]
  ));
  const [evidence, setEvidence] = useState(null);
  const [checking, setChecking] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState("");
  // "Edit as code": the same signed spec the Connect button sends, as text.
  // codeDirty is what stops the live mirror from overwriting an edit in
  // progress — until the operator applies or discards it, the textarea is
  // theirs and the form no longer writes into it.
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeText, setCodeText] = useState("");
  const [codeDirty, setCodeDirty] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [codeNotice, setCodeNotice] = useState("");
  const errorRef = useRef(null);
  const hardwareProfile = String(gateway.hardware_profile || "");
  const serialHardware = SERIAL_HARDWARE_GUIDANCE[hardwareProfile] || null;
  const usesTCP = kind === "modbus-tcp" || kind === "modbus-rtu-over-tcp";
  const unitIDBounds = modbusUnitIDBounds(kind);
  const turck = kind === "modbus-tcp" && settings.deviceTemplate === "turck-tben-s1-8dxp";
  const locked = busy || reviewing;

  const existingPaths = useMemo(() => points
    .filter((point) => point.tagMode === "existing" && point.tagPath)
    .map((point) => point.tagPath), [points]);
  const evidenceKey = existingPaths.join("\u0000");
  const serialPortsKey = serialPorts.join(" ");

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (kind !== "modbus-rtu" || settings.serialPort || !serialPorts.length) return;
    const preferred = serialPorts.find((path) => String(path).includes("/dev/serial/by-path/")) || serialPorts[0];
    setSettings((current) => ({ ...current, serialPort: preferred }));
  }, [kind, serialPorts, settings.serialPort]);

  useEffect(() => {
    setEvidence(null);
    setEvidenceError("");
    if (!existingPaths.length) {
      // If the last existing tag is cleared or changed to "Create new" while
      // its catalog request is still running, that request's cleanup is
      // intentionally ignored as stale. Clear the spinner here so the form
      // cannot remain permanently locked in "Checking".
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    setChecking(true);
    inspectEdgeTagCatalog(gateway.id, existingPaths)
      .then((payload) => { if (!cancelled) setEvidence(payload); })
      .catch(() => { if (!cancelled) setEvidenceError(`Could not verify these tags on ${gateway.name}. Try again.`); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
    // Fetch only when the exact existing-tag set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceKey, gateway.id, gateway.name]);

  const updatePoint = (id, patch) => setPoints((current) => current.map((point) => (
    point.id === id ? { ...point, ...patch } : point
  )));
  const updatePointSettings = (id, patch) => setPoints((current) => current.map((point) => (
    point.id === id ? { ...point, settings: { ...point.settings, ...patch } } : point
  )));
  const addPoint = () => {
    if (points.length >= MAX_POINTS) return;
    const id = `point-${nextID.current}`;
    nextID.current += 1;
    setPoints((current) => [...current, initialPoint(id)]);
  };
  const removePoint = (id) => setPoints((current) => current.filter((point) => point.id !== id));

  // buildFromForm turns the live form into the same signed spec the Connect
  // button sends, without touching the network. Connect needs it; so does the
  // code editor, which shows exactly what would be previewed rather than a
  // second rendering of the same settings that could drift from it.
  const buildFromForm = useCallback(() => {
    const name = deviceName.trim();
    if (!name) return { error: "Name this Modbus device." };
    if (kind === "modbus-rtu" && serialHardware && !serialPorts.includes(settings.serialPort)) {
      return { error: `The selected serial port is not detected on ${gateway.name}. Check the runtime installation and hardware.` };
    }
    const pendingTags = [];
    const preparedPoints = [];
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      let tagPath = point.tagPath;
      let tagType = "";
      let tagWritable = true;
      if (point.tagMode === "new") {
        const slug = tagSlug(point.newTagName);
        if (!slug) return { error: `Point ${index + 1}: Name the new tag.` };
        tagPath = newTagFullPath(point.newTagFolder, point.newTagName);
        tagType = point.newTagType;
      } else {
        if (!tagPath) return { error: `Point ${index + 1}: Choose a tag.` };
        const eligibility = edgeConnectorTagEligibility(evidence, tagPath);
        if (!eligibility.eligible) {
          return { error: `Point ${index + 1}: ${eligibility.message || evidenceError || "This tag is not ready on the device."}` };
        }
        tagType = eligibility.tagType;
        tagWritable = eligibility.writable;
      }
      const allowedFunctions = new Set(modbusFunctionOptions(tagType).map((option) => option.value));
      const normalizedSettings = {
        ...point.settings,
        function: allowedFunctions.has(String(point.settings.function ?? "")) ? point.settings.function : "",
      };
      if (tagType === "integer" || tagType === "number") {
        const formats = modbusValueTypeOptions(tagType).map(({ value }) => value);
        if (!formats.includes(normalizedSettings.valueType)) {
          normalizedSettings.valueType = tagType === "number" ? "float32" : "int16";
        }
      }
      // Derived, never asked: the address type and the tag's own writability
      // decide this, exactly as they do on the runtime.
      const direction = turck
        ? normalizedSettings.channelMode === "output" ? "output" : "input"
        : modbusPointDirection(tagType, normalizedSettings, tagWritable);
      if (point.tagMode === "existing" && direction === "output" && tagWritable !== true) {
        return { error: `Point ${index + 1}: This tag is read only on ${gateway.name}. Choose a writable tag or a read-only Modbus point.` };
      }
      if (point.tagMode === "new") {
        pendingTags.push({
          fullPath: tagPath,
          // The path POST /api/tags takes is everything under the project root,
          // so a folder has to travel with the name.
          path: tagPath.split("/").slice(1).join("/"),
          name: point.newTagName.trim() || tagSlug(point.newTagName),
          type: TAG_TYPE_WIRE[tagType],
          writable: direction === "output",
          description: `Created for ${name}`,
          direction,
        });
      }
      preparedPoints.push({ tagPath, tagType, direction, settings: normalizedSettings });
    }
    try {
      return {
        spec: buildEdgeModbusConnectorSpec({
          kind,
          name,
          connectorID: initialDraft?.connector_id || "",
          desiredState: initialDraft?.desired_state || "running",
          settings,
          points: preparedPoints,
        }),
        pendingTags,
      };
    } catch (buildError) {
      return { error: buildError.message };
    }
    // serialPortsKey, not serialPorts: the prop defaults to a fresh [] on every
    // render, and depending on its identity would rebuild this callback each
    // time — which would make the code mirror below run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceName, kind, serialHardware, serialPortsKey, settings, points, turck, evidence, evidenceError,
    gateway.name, initialDraft]);

  // While the code panel is open and untouched it mirrors the form, so an
  // operator can change a setting above and watch the document that will
  // actually be sent change with it. It rewrites the text and the parse error,
  // never the notice: the notice belongs to the action that produced it
  // (Validate, Apply, Export), and this effect fires on the state changes those
  // actions themselves make.
  useEffect(() => {
    if (!codeOpen || codeDirty) return;
    const built = buildFromForm();
    setCodeText(built.spec ? formatEdgeModbusSpec(built.spec) : "");
    setCodeError(built.error || "");
  }, [codeOpen, codeDirty, buildFromForm]);

  const openCode = (copy = false) => {
    const built = buildFromForm();
    setCodeOpen(true);
    setCodeDirty(false);
    setCodeText(built.spec ? formatEdgeModbusSpec(built.spec) : "");
    setCodeError(built.error || "");
    setCodeNotice("");
    if (!copy || !built.spec) return;
    // Export is "show it and put it on the clipboard". A browser that refuses
    // the clipboard still leaves the document on screen to copy by hand.
    Promise.resolve(navigator?.clipboard?.writeText?.(formatEdgeModbusSpec(built.spec)))
      .then(() => setCodeNotice("Copied this connection as code to the clipboard."))
      .catch(() => setCodeNotice("Select the code below and copy it — this browser blocked the clipboard."));
  };

  const validateCode = () => {
    setCodeNotice("");
    try {
      const { spec } = parseEdgeModbusSpecCode(codeText);
      setCodeError("");
      setCodeNotice(`Valid — ${spec.mappings.length} ${spec.mappings.length === 1 ? "point" : "points"} on "${spec.name}". Nothing has been sent to ${gateway.name}.`);
      return spec;
    } catch (parseError) {
      setCodeError(String(parseError?.message || parseError));
      return null;
    }
  };

  const applyCode = () => {
    setCodeNotice("");
    let parsed;
    try {
      parsed = parseEdgeModbusSpecCode(codeText);
    } catch (parseError) {
      setCodeError(String(parseError?.message || parseError));
      return;
    }
    if (parsed.kind !== kind) {
      // The form's transport decides which questions it asks, and the parent
      // chose it. Silently reinterpreting a serial device as a TCP one would
      // discard settings the document states.
      setCodeError(`This code describes a ${edgeModbusSpecKind(parsed.spec)} device. Cancel and start a connection of that type to use it.`);
      return;
    }
    const draft = edgeModbusSpecToDraft(parsed.spec);
    setDeviceName(draft.name);
    setSettings((current) => ({ ...current, ...draft.settings, kind }));
    setPoints(draft.points.map((point, index) => initialPoint(`point-${index + 1}`, point)));
    nextID.current = draft.points.length + 1;
    setCodeError("");
    setCodeDirty(false);
    setError("");
    setCodeNotice(`Filled the form from the code — ${draft.points.length} ${draft.points.length === 1 ? "point" : "points"}. Review it, then select ${initialDraft?.connector_id ? "Save changes" : "Connect"}.`);
  };

  const review = async () => {
    setError("");
    const built = buildFromForm();
    if (built.error) { setError(built.error); return; }
    const { spec, pendingTags } = built;
    if (pendingTags.length) {
      if (Number.isSafeInteger(initialDraft?.expected_revision)) {
        onPreview?.(spec, pendingTags, initialDraft.expected_revision);
      } else {
        onPreview?.(spec, pendingTags);
      }
      return;
    }
    setReviewing(true);
    try {
      const preview = await previewEdgeConnector(gateway.id, spec, initialDraft?.expected_revision);
      onPreview?.(preview, []);
    } catch (previewError) {
      const message = publicConnectionError(previewError, gateway.name);
      setError(message);
      notify?.({ type: "error", title: "Device could not be prepared", message });
    } finally {
      setReviewing(false);
    }
  };

  return (
    <>
      {error ? (
        <div className="gwAlert" role="alert" tabIndex={-1} ref={errorRef}>
          <div className="gwAlertTitle">Check the Modbus device</div>
          <div className="gwAlertBody">{error}</div>
        </div>
      ) : null}
      <fieldset className="gwConnectionBuilderFields" disabled={locked}>
        <div className="gwModbusCodeBar">
          <button type="button" className="gwGhostBtn" aria-expanded={codeOpen} onClick={() => (codeOpen ? setCodeOpen(false) : openCode(false))}>
            {codeOpen ? "Hide code" : "Edit as code"}
          </button>
          {initialDraft?.connector_id ? (
            <button type="button" className="gwGhostBtn" onClick={() => openCode(true)}>Export as code</button>
          ) : null}
        </div>
        {codeOpen ? (
          <div className="gwConnectionFormGroup gwModbusCodePanel">
            <div>
              <div className="gwLabel">Connection as code</div>
              <div className="gwHint">
                This is the exact device configuration Connect sends for review. Edit it, select Validate, then
                Apply to form — nothing reaches {gateway.name} until you select {initialDraft?.connector_id ? "Save changes" : "Connect"}.
              </div>
            </div>
            <textarea
              className="gwInput gwCodeArea"
              aria-label="Connection as code"
              spellCheck={false}
              rows={16}
              value={codeText}
              onChange={(event) => { setCodeText(event.target.value); setCodeDirty(true); setCodeNotice(""); }}
            />
            {codeError ? (
              <div className="gwAlert" role="alert">
                <div className="gwAlertTitle">Check the code</div>
                <div className="gwAlertBody">{codeError}</div>
              </div>
            ) : null}
            {codeNotice ? <div className="gwHint isPositive" role="status">{codeNotice}</div> : null}
            <div className="gwHeaderActions">
              <button type="button" className="gwGhostBtn" onClick={validateCode}>Validate</button>
              <button type="button" className="gwSecondaryBtn" onClick={applyCode}>Apply to form</button>
            </div>
          </div>
        ) : null}
        <div className="gwConnectionFormGroup">
          <div>
            <div className="gwLabel">Device settings</div>
            <div className="gwHint">These settings apply to every point on this device.</div>
          </div>
          {/* RTU shows eight settings and TCP six, so each transport gets the
              column count that divides evenly. Auto-fit left both with a
              ragged last row at this panel's width. */}
          <div
            className="gwConnectionFields gwConnectionDeviceFields"
            style={{ "--gw-device-columns": kind === "modbus-rtu" ? "4" : "3" }}
          >
            <Field label="Device name" help="For example, Room sensor or Relay board.">
              <input className="gwInput" value={deviceName} onChange={(event) => setDeviceName(event.target.value)} placeholder="Modbus device name" />
            </Field>
            {usesTCP ? (
              <>
                {kind === "modbus-tcp" ? <Field label="Device model"><Select value={settings.deviceTemplate} options={Object.entries(MODBUS_DEVICE_TEMPLATES).map(([value, item]) => ({ value, label: item.label }))} onChange={(deviceTemplate) => setSettings((current) => ({ ...current, deviceTemplate }))} /></Field> : null}
                <Field label="Device host"><input className="gwInput" value={settings.host} onChange={(event) => setSettings((current) => ({ ...current, host: event.target.value }))} placeholder="10.0.0.21" /></Field>
                <Field label="TCP port"><NudgeNumber value={settings.port} onChange={(port) => setSettings((current) => ({ ...current, port }))} min={1} max={65535} aria-label="TCP port" /></Field>
              </>
            ) : (
              <>
                <Field
                  label="Serial port"
				  help={serialPorts.length
					? serialHardware?.portHelp || "Detected on this device. Stable paths keep working after a reboot."
					: "No stable serial port was detected. USB adapters need the detected /dev/serial/by-path name."}
                  warning={serialPorts.length && settings.serialPort && !serialPorts.includes(settings.serialPort) ? "This port is no longer reported by the device." : ""}
                >
                  {serialPorts.length ? (
                    <Select value={settings.serialPort} options={serialPorts.map((path) => ({ value: path, label: serialPortLabel(path, hardwareProfile) }))} onChange={(serialPort) => setSettings((current) => ({ ...current, serialPort }))} />
                  ) : serialHardware ? (
                    <input className="gwInput" value={serialHardware.missing} readOnly aria-readonly="true" />
                  ) : (
					<input className="gwInput" value={settings.serialPort} onChange={(event) => setSettings((current) => ({ ...current, serialPort: event.target.value }))} placeholder="/dev/serial/by-path/…" />
                  )}
                </Field>
                <Field label="Baud rate" help="Pick a common rate or type the exact value from the device manual.">
                  <ComboInput value={settings.baudRate} onChange={(baudRate) => setSettings((current) => ({ ...current, baudRate }))} options={SERIAL_BAUD_RATES} ariaLabel="Baud rate" placeholder="19200" />
                </Field>
                <Field label="Parity"><Select value={settings.parity} options={[{ value: "even", label: "Even" }, { value: "odd", label: "Odd" }, { value: "none", label: "None" }]} onChange={(parity) => setSettings((current) => ({ ...current, parity }))} /></Field>
                <Field label="Data bits"><input className="gwInput" value="8" readOnly aria-readonly="true" /></Field>
                <Field label="Stop bits"><Select value={settings.stopBits} options={[{ value: "1", label: "1 (standard)" }, { value: "2", label: "2" }]} onChange={(stopBits) => setSettings((current) => ({ ...current, stopBits }))} /></Field>
              </>
            )}
            <Field label="Device address" help="Often called Unit ID or slave address."><NudgeNumber value={settings.unitId} onChange={(unitId) => setSettings((current) => ({ ...current, unitId }))} min={unitIDBounds.min} max={unitIDBounds.max} aria-label="Device address" /></Field>
            <Field label="Timeout (ms)"><NudgeNumber value={settings.timeoutMs} onChange={(timeoutMs) => setSettings((current) => ({ ...current, timeoutMs }))} min={50} max={60000} aria-label="Timeout (ms)" /></Field>
          </div>
		  {kind === "modbus-rtu" ? <div className="gwHint">Control Seat must be the only Modbus master on this serial bus. Multiple field devices are supported when each has a unique address.</div> : null}
        </div>

        <div className="gwConnectionFormGroup gwModbusPoints">
          <div className="gwModbusPointsHeader">
            <div><div className="gwLabel">Device points</div><div className="gwHint">Add every value and output exposed by this Modbus device.</div></div>
            <button type="button" className="gwSecondaryBtn" onClick={addPoint} disabled={locked || points.length >= MAX_POINTS}>Add point</button>
          </div>
          {points.map((point, index) => {
            const eligibility = point.tagMode === "existing" && point.tagPath
              ? edgeConnectorTagEligibility(evidence, point.tagPath)
              : null;
            const tagType = point.tagMode === "new" ? point.newTagType : eligibility?.tagType || "";
            // A tag we are about to create is made writable when the address
            // supports it, so only an existing tag can veto the write.
            const tagWritable = point.tagMode === "new" ? true : eligibility?.writable;
            return (
              <section className="gwModbusPointCard" key={point.id} aria-label={`Modbus point ${index + 1}`}>
                <div className="gwModbusPointHeader">
                  <strong>Point {index + 1}</strong>
                  {points.length > 1 ? <button type="button" className="gwGhostBtn" onClick={() => removePoint(point.id)}>Remove</button> : null}
                </div>
                <ModbusTagField
                  point={point}
                  gateway={gateway}
                  locked={locked}
                  eligibility={eligibility}
                  checking={checking && point.tagMode === "existing" && Boolean(point.tagPath)}
                  evidenceError={evidenceError}
                  updatePoint={(patch) => updatePoint(point.id, patch)}
                />
                <ModbusPointFields
                  point={point}
                  turck={turck}
                  tagType={tagType}
                  tagWritable={tagWritable}
                  updateSettings={(patch) => updatePointSettings(point.id, patch)}
                />
              </section>
            );
          })}
        </div>
      </fieldset>
      <footer className="gwConnectionBuilderFooter">
        <div className="gwHint">Connect checks these settings, creates any new tags, then sends the device to {gateway.name}.</div>
        <div className="gwHeaderActions">
          <button type="button" className="gwGhostBtn" disabled={locked} onClick={onCancel}>Cancel</button>
          <button type="button" className="gwPrimaryBtn" disabled={locked || checking || !deviceName.trim() || points.length === 0} onClick={review}>
            {reviewing || busy
              ? (initialDraft?.connector_id ? "Saving…" : "Connecting…")
              : initialDraft?.connector_id ? "Save changes" : "Connect"}
          </button>
        </div>
      </footer>
    </>
  );
}
